import type { Coding } from '@medplum/fhirtypes'
import type { Observation } from '@medplum/fhirtypes'
import {
  type DerivationRule,
  rulesForTarget,
  selectedRule,
} from '@seen/core/observation-logic/derivations/rules'
import { STARTER_DERIVATIONS } from '@seen/core/observation-logic/derivations/starter'
import {
  instrumentAnswerKey,
  instrumentMemberKey,
} from '@seen/core/observation-logic/instruments/records'
import { bandSetsFor } from '@seen/core/observation-logic/interpretation'
import {
  nodeDraft,
  panelAnswers,
} from '@seen/core/observation-logic/noteCapture'
import type { ObservationValue } from '@seen/core/observation-logic/value'
import { panelTemplate } from '@seen/core/terminology/instruments/loincPanels'
import { instrumentItem } from '@seen/core/terminology/instruments/registry'
import type { BandSet } from '@seen/core/terminology/interpretations'
import { observationTemplate } from '@seen/core/terminology/observationTemplates'
import type { Node as PMNode } from '@tiptap/pm/model'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'

const OBSERVATION_NODE = 'observation'
const OBSERVATION_PANEL_NODE = 'observation-panel'

// A panel's answer to `coding` as an input source, when the panel asks it.
function panelSource(
  node: PMNode,
  coding: Coding
): { source: NoteInputSource; answered: boolean } | undefined {
  const attrs = node.attrs
  const template = panelTemplate(attrs)
  const item = template ? instrumentItem(template, coding) : undefined
  const nodeId = typeof attrs.id === 'string' ? attrs.id : undefined
  if (!item || !nodeId) {
    return undefined
  }
  const groupId =
    typeof attrs.groupId === 'string' && attrs.groupId ? attrs.groupId : nodeId
  const member = instrumentMemberKey(groupId, 'item', item.coding)
  const held = panelAnswers(attrs)
  if (held) {
    const value = held[instrumentAnswerKey(item.coding)] ?? undefined
    const heldPanel: NonNullable<NoteInputSource['panel']> = { nodeId, member }
    if (value) {
      heldPanel.value = value
    }
    return {
      source: { coding, reference: null, panel: heldPanel },
      answered: !!value,
    }
  }
  // Answers written as given: the item's record is found by its member key.
  return {
    source: { coding, reference: null, panel: { nodeId, member } },
    answered: typeof attrs.groupId === 'string' && !!attrs.groupId,
  }
}

// The calculation a capturing node's code has, the node's choice among
// several first. Undefined for a code recorded by hand, and for a node that
// shows the latest record instead of capturing.
export function derivedRuleFor(
  attributes: Pick<
    ObservationNodeAttributes,
    'system' | 'code' | 'mode' | 'derivedRuleId'
  >,
  rules: readonly DerivationRule[] = STARTER_DERIVATIONS
): DerivationRule | undefined {
  if (attributes.mode === 'latest') {
    return undefined
  }
  return selectedRule(
    { system: attributes.system, code: attributes.code },
    rules,
    attributes.derivedRuleId ?? undefined
  )
}

export function hasDerivation(
  attributes: Pick<ObservationNodeAttributes, 'system' | 'code' | 'mode'>,
  rules: readonly DerivationRule[] = STARTER_DERIVATIONS
): boolean {
  return (
    attributes.mode !== 'latest' &&
    rulesForTarget({ system: attributes.system, code: attributes.code }, rules)
      .length > 0
  )
}

// Where an input is in the note: the nearest node that records the code,
// either itself or as a part (systolic in a blood pressure). `reference` is
// null while that node is unanswered.
export interface NoteInputSource {
  coding: Coding
  reference: string | null
  // The part of the record that holds the value, for a part.
  component?: Coding
  // Every node for the code is hidden by a condition: the input counts as
  // unanswered, and the participant's chart is not read in its place.
  hidden?: true
  // The node's id and held answer when it holds one in the note (deferred
  // capture): the value comes from the draft, with no record to read.
  nodeId?: string
  draft?: Observation
  // An item of a panel in the note: its member key, which finds the item's
  // record, and its answer when the panel holds answers until signing.
  panel?: { nodeId: string; member: string; value?: ObservationValue }
}

function sameConcept(
  attributes: { system?: string | null; code?: string | null },
  coding: Coding
): boolean {
  return (
    attributes.code === coding.code &&
    (!attributes.system ||
      !coding.system ||
      attributes.system === coding.system)
  )
}

interface Candidate {
  source: NoteInputSource
  distance: number
  answered: boolean
}

// For each input, the nearest answered node (before the computed one
// winning a tie), else the nearest unanswered one, preferring before it
// (after it is where Add puts missing inputs). Nodes hidden by a condition
// are passed over; an input found only in hidden nodes comes back marked.
export function noteInputSources(
  doc: PMNode,
  position: number,
  inputs: readonly Coding[],
  isHidden: (pos: number) => boolean = () => false
): (NoteInputSource | undefined)[] {
  const candidates: Candidate[][] = inputs.map(() => [])
  const hiddenOnly: boolean[] = inputs.map(() => false)
  doc.descendants((node, pos) => {
    if (node.type.name === OBSERVATION_PANEL_NODE) {
      if (node.attrs.mode === 'latest') {
        return false
      }
      const hidden = isHidden(pos)
      inputs.forEach((coding, index) => {
        const found = panelSource(node, coding)
        if (!found) {
          return
        }
        if (hidden) {
          hiddenOnly[index] = true
          return
        }
        candidates[index]?.push({
          ...found,
          distance: Math.abs(position - pos) * 2 + (pos > position ? 1 : 0),
        })
      })
      return false
    }
    if (node.type.name !== OBSERVATION_NODE) {
      return true
    }
    const attrs = node.attrs as ObservationNodeAttributes
    if (pos === position || attrs.mode === 'latest') {
      return false
    }
    const hidden = isHidden(pos)
    const draft = nodeDraft(node.attrs) ?? undefined
    inputs.forEach((coding, index) => {
      const part = sameConcept(attrs, coding)
        ? undefined
        : observationTemplate(attrs)?.components.find((spec) =>
            sameConcept(spec.coding, coding)
          )
      if (!sameConcept(attrs, coding) && !part) {
        return
      }
      if (hidden) {
        hiddenOnly[index] = true
        return
      }
      const source: NoteInputSource = { coding, reference: attrs.reference }
      if (draft && attrs.id) {
        source.nodeId = attrs.id
      }
      if (draft) {
        source.draft = draft
      }
      if (part) {
        source.component = part.coding
      }
      candidates[index]?.push({
        source,
        // Before the computed node sorts ahead of after at equal distance.
        distance: Math.abs(position - pos) * 2 + (pos > position ? 1 : 0),
        answered: !!attrs.reference || !!draft,
      })
    })
    return false
  })
  return inputs.map((coding, index) => {
    const found = candidates[index] ?? []
    const nearest = (list: Candidate[]): Candidate | undefined =>
      list.reduce<Candidate | undefined>(
        (best, candidate) =>
          !best || candidate.distance < best.distance ? candidate : best,
        undefined
      )
    const answered = nearest(found.filter((candidate) => candidate.answered))
    if (answered) {
      return answered.source
    }
    const before = nearest(
      found.filter((candidate) => candidate.distance % 2 === 0)
    )
    const after = found.find((candidate) => candidate.distance % 2 === 1)
    const unanswered = before ?? after
    if (unanswered) {
      return unanswered.source
    }
    return hiddenOnly[index]
      ? { coding, reference: null, hidden: true }
      : undefined
  })
}

export interface DerivedNodeAttributes extends Pick<
  ObservationNodeAttributes,
  | 'derivedOverride'
  | 'derivedRuleId'
  | 'derivedBandsId'
  | 'system'
  | 'code'
  | 'mode'
> {}

// The published cutoff sets a computed node can band its value by.
export function derivationCutoffs(
  attributes: DerivedNodeAttributes
): readonly BandSet[] {
  if (!hasDerivation(attributes)) {
    return []
  }
  return bandSetsFor({ system: attributes.system, code: attributes.code })
}

// The rules a computed node can choose between.
export function derivationMethods(
  attributes: DerivedNodeAttributes
): DerivationRule[] {
  if (attributes.mode === 'latest') {
    return []
  }
  return rulesForTarget(
    { system: attributes.system, code: attributes.code },
    STARTER_DERIVATIONS
  )
}
